import React, { useEffect, useRef, useState } from 'react';
import {
  AriaRangeCalendarProps,
  useCalendarCell,
  useCalendarGrid,
  useRangeCalendar,
} from '@react-aria/calendar';
import {
  RangeCalendarState,
  useRangeCalendarState,
} from '@react-stately/calendar';
import {
  CalendarDate,
  createCalendar,
  endOfMonth,
  getWeeksInMonth,
  isEqualDay,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  endOfWeek,
  today,
} from '@internationalized/date';
import { I18nProvider, useDateFormatter, useLocale } from '@react-aria/i18n';
import { ButtonProps, DateValue } from 'react-aria-components';
import { mergeProps } from '@react-aria/utils';
import { useFocusRing } from '@react-aria/focus';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { DOMAttributes, FocusableElement, RangeValue } from '@react-types/shared';
import { useButton } from '@react-aria/button';
import './RangeCalendar.scss';
import {ReactComponent as Arrow} from "img/arrow-right.svg"


export default function RangeCalendar(
  props: AriaRangeCalendarProps<DateValue> & {
    value?: RangeValue<DateValue> | null;
    onChange?: (range: RangeValue<DateValue> | null) => void;
  },
) {
  const { locale } = useLocale();
  const state = useRangeCalendarState({
    ...props,
    visibleDuration: { months: 2 },
    locale,
    createCalendar,
  });

  const ref = useRef<HTMLDivElement>(null);
  const { calendarProps, prevButtonProps, nextButtonProps } = useRangeCalendar(props, state, ref);

  // useEffect(() => {
  //   if (props.onChange && state.value) {
  //     props.onChange(state.value);
  //   }
  // }, [state.value, props]);

  const todayDate = today(state.timeZone);
  const setRange = (range: RangeValue<DateValue>) => {
    state.setValue(range);
  };

  return (
    <I18nProvider locale="en-US">
      <div {...calendarProps} ref={ref} className="range-calendar1">
        <div className="calendar-shortcuts">
          <button onClick={() => setRange({ start: todayDate, end: todayDate })}>Today</button>
          <button onClick={() => setRange({ start: todayDate.subtract({ days: 1 }), end: todayDate.subtract({ days: 1 }) })}>Yesterday</button>
          <button onClick={() => setRange({ start: startOfWeek(todayDate, locale), end: endOfWeek(todayDate, locale) })}>This week</button>
          <button onClick={() => setRange({ start: startOfMonth(todayDate), end: endOfMonth(todayDate) })}>This month</button>
          <button onClick={() => setRange({ start: todayDate.subtract({ days: 7 }), end: todayDate })}>Last 7 days</button>
          <button onClick={() => setRange({
            start: startOfMonth(todayDate).subtract({ months: 1 }),
            end: endOfMonth(todayDate).subtract({ months: 1 }),
          })}>Last month</button>
        </div>
        <div className="calendar-main">
          <div className="calendar-grids">
            <div className="calendar-panel">
              <CalendarHeader
                state={state}
                calendarProps={calendarProps}
                prevButtonProps={prevButtonProps}
                nextButtonProps={nextButtonProps}
              />
            </div>
            <div className="calendar-panel">
              <CalendarGrid state={state} offset={0} />
              <CalendarGrid state={state} offset={1} />
            </div>
          </div>
        </div>
      </div>
    </I18nProvider>
  );
}

function CalendarHeader({
  state,
  calendarProps,
  prevButtonProps,
  nextButtonProps,
}: {
  state: RangeCalendarState;
  calendarProps: DOMAttributes<FocusableElement>;
  prevButtonProps: ButtonProps;
  nextButtonProps: ButtonProps;
}) {
  const formatter = useDateFormatter({ month: 'long', year: 'numeric', timeZone: state.timeZone });

  const getFormatted = (date: CalendarDate) =>
    formatter.format(date.toDate(state.timeZone));

  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const { buttonProps: prevProps } = useButton(prevButtonProps, prevRef);
  const { buttonProps: nextProps } = useButton(nextButtonProps, nextRef);

  return (
    <div className="calendar-header">
      <VisuallyHidden>
        <h2>{calendarProps['aria-label']}</h2>
      </VisuallyHidden>
      <button {...prevProps} ref={prevRef} className="calendar-button">
        <Arrow className='reverse-arrow default-svg-color'/>
      </button>
      <div className="calendar-title">{getFormatted(state.visibleRange.start)}</div>
      <div className="calendar-title">{getFormatted(state.visibleRange.start.add({ months: 1 }))}</div>
      <button {...nextProps} ref={nextRef} className="calendar-button">
        <Arrow className='default-svg-color'/>
      </button>
    </div>
  );
}

function CalendarGrid({ state, offset }: { state: RangeCalendarState; offset: number }) {
  const { locale } = useLocale();
  const startDate = state.visibleRange.start.add({ months: offset });
  const weeksInMonth = getWeeksInMonth(startDate, locale);
  const { gridProps, headerProps } = useCalendarGrid({ startDate }, state);

  return (
    <table {...gridProps} className="calendar-grid">
      <thead {...headerProps}>
        <tr>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day, i) => (
            <th key={i} className="calendar-day">{day}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: weeksInMonth }).map((_, weekIndex) => (
          <tr key={weekIndex}>
            {state.getDatesInWeek(weekIndex, startDate).map((date, i) =>
              date ? (
                <CalendarCell key={i} state={state} date={date} currentMonth={startDate} />
              ) : (
                <td key={i} className="calendar-empty" />
              )
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CalendarCell({
  state,
  date,
  currentMonth,
}: {
  state: RangeCalendarState;
  date: CalendarDate;
  currentMonth: DateValue;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const {
    cellProps,
    buttonProps,
    isSelected,
    isDisabled,
    formattedDate,
  } = useCalendarCell({ date }, state, ref);
  const { focusProps, isFocusVisible } = useFocusRing();
  const now = today(state.timeZone);

  const isToday = isEqualDay(date, now);
  const isOutsideMonth = !isSameMonth(currentMonth, date);

  const isSelectionStart = state.highlightedRange
    ? isSameDay(date, state.highlightedRange.start)
    : isSelected;
  const isSelectionEnd = state.highlightedRange
    ? isSameDay(date, state.highlightedRange.end)
    : isSelected;

  return (
    <td {...cellProps} className="calendar-cell">
      <div
        ref={ref}
        hidden={isOutsideMonth}
        {...mergeProps(buttonProps, focusProps)}
        className={`calendar-date
          ${isSelected ? 'selected' : ''}
          ${isToday ? 'today' : ''}
          ${isDisabled ? 'disabled' : ''}
          ${isSelectionStart ? 'range-start' : ''}
          ${isSelectionEnd ? 'range-end' : ''}
        `}
      >
        {formattedDate}
      </div>
    </td>
  );
}
